import * as THREE from 'three';
import JSZip from "jszip";
import Engine from "./Engine";
import MaterialAsset from "./MaterialAsset";
import Scenario from './scenarioElements/Scenario';
import {GroupsManager} from './GroupsManager';
import ARTracker from "./ARTracker";
import DefaultSceneManager from "./DefaultSceneManager";
import {assert} from "./utilities/ErrorHandling";
import MathUtils from "./utilities/MathUtils";
import AssetUtility from "./AssetUtility";
import {Timeline} from "./timeline/Timeline";
import {JsonCopy} from "./utilities/Misc";
import SceneObjectUtils from './utilities/SceneObjectUtils';
import { checkModification, updateObjectActiveModifications } from './utilities/PropertyComparisonUtils';
import TemplateTypes from '../components/uisystem/templates/types';
import {CustomEventTypes, SaveModificationTypes} from "../globals";
import store, {StoreActions} from '../state/store';
import {getAssetsFromGrapesJS} from "../components/visageUI/GrapesJSUtilities";
import {UIData} from "../interfaces/UITemplateData";
import {IChecklistData} from "../interfaces/checklistItem";
import {IUITemplateDefinition} from "../interfaces/Assets";
import {IAttachements} from "../interfaces/Attachments";
import {IGrapesJSProjectData, IVisageSaveData} from "../interfaces/VisageUI";
import {
	IDefaultObjectLink,
	IModelData,
	IModifiedChildrenRootNode,
	INodeSaveData,
	IProjectSaveData,
	ISceneSaveData,
	IStepConditionsSaveData,
	ITrackerSaveData
} from '../interfaces/ProjectSave';
import { IMetaDataObject } from '../interfaces/Misc';

const SAVE_VERSION = 2.1;

export default class ProjectSaver
{
	private static instance: ProjectSaver;

	private savedSceneGuids: string[];
	private objectGuidToSaveData: Map<string, INodeSaveData>;

	static getInstance()
	{
		if (!ProjectSaver.instance) ProjectSaver.instance = new ProjectSaver();
		return ProjectSaver.instance;
	}

	constructor()
	{
		this.savedSceneGuids = [];
		this.objectGuidToSaveData = new Map<string, INodeSaveData>();
	}

	markAsSaveRoot(object: THREE.Object3D): void
	{
		if (object instanceof ARTracker) return;
		object.userData.markedSaveRoot = true;
	}

	markModifiedObject(object: THREE.Object3D, saveModificationType: SaveModificationTypes): void
	{
		if (object instanceof ARTracker || object.userData.hideInHierarchy) return;
		switch (saveModificationType)
		{
			case SaveModificationTypes.Removed:
				if (object.userData.markedSaveRoot) return;
				assert(object.userData.modelData);
				const rootParent = this.getParentSaveRoot(object);
				assert(rootParent.userData.assetId && rootParent.userData.assetId === object.userData.assetId);
				if (!rootParent.userData.removedSubNodes) rootParent.userData.removedSubNodes = new Array<IModelData>();
				rootParent.userData.removedSubNodes.push({ nodeIndex: object.userData.modelData.nodeIndex, primitiveIndex: object.userData.modelData.primitiveIndex });
				object.userData.ownerSaveRoot = rootParent.userData.guid;

				break;
			case SaveModificationTypes.ReAdded:
				if (object.userData.ownerSaveRoot)
				{
					const rootParent = SceneObjectUtils.findObjectByGuid(Engine.getInstance().getSelectionRoot(), object.userData.ownerSaveRoot);
					delete object.userData.ownerSaveRoot;
					if (rootParent)
					{
						for (let i = 0; i < rootParent.userData.removedSubNodes.length; i++)
						{
							const removedSubNode = rootParent.userData.removedSubNodes[i] as IModelData;
							if (removedSubNode.nodeIndex === object.userData.modelData.nodeIndex && removedSubNode.primitiveIndex === object.userData.modelData.primitiveIndex)
							{
								rootParent.userData.removedSubNodes.splice(i, 1);
								break;
							}
						}
					}

				}
				break;
			case SaveModificationTypes.ReorderedChildren:
				if (object.parent?.type === 'Scene')
				{
					return;
				}
			// eslint-disable-next-line no-fallthrough
			default:
				const defaultObject = DefaultSceneManager.getInstance().getDefaultObject(object.userData.guid);
				if (!object.userData.saveModifications) object.userData.saveModifications = [];
				if (!object.userData.saveModifications.includes(saveModificationType)) object.userData.saveModifications.push(saveModificationType);
				if (defaultObject)
				{
					updateObjectActiveModifications(object, defaultObject);
				}
				else 
				{
					const linkedObjects = DefaultSceneManager.getInstance().getLinkedObjects(object);
					linkedObjects.forEach((linkedObject) => 
					{
						updateObjectActiveModifications(linkedObject, object);
					});
				}
				document.dispatchEvent(new CustomEvent(CustomEventTypes.updateObjectModified));
		}
	}

	async save()
	{
		const jsZip = new JSZip();
		const scenesToSave = Scenario.getAllScenes();
		scenesToSave.sort((a: THREE.Scene, b: THREE.Scene) => { return a.userData.isDefaultScene ? -1 : 1; });

		let scenesSaveData: ISceneSaveData[] = [];
		const modelAssetIds: string[] = [];
		const audioAssetIds: string[] = [];

		this.savedSceneGuids = scenesToSave.map((scene) => scene.userData.guid);

		for (const scene of scenesToSave)
		{
			const sceneData: ISceneSaveData =
			{
				guid: scene.userData.guid,
				nodes: [],
				childOrder: [],
				...(scene.userData.defaultSceneGuid && { defaultScene: scene.userData.defaultSceneGuid }),
				...(scene.userData.isDefaultScene &&
				{
					isDefaultScene: true,
					linkedScenes: DefaultSceneManager.getInstance().getLinkedScenesGuids(scene.userData.guid).filter((sceneGuid) => this.savedSceneGuids.includes(sceneGuid)),
				}),
			};
			const root = Engine.getInstance().getSelectionRoot(scene.userData.guid);
			for (const rootChild of root.children)
			{
				if (rootChild.userData.markedSaveRoot)
				{
					sceneData.childOrder.push({ guid: rootChild.userData.guid });
				}
			}

			scenesSaveData.push(sceneData);
			root.traverse((child) =>
			{
				if (child.userData.markedSaveRoot)
				{
					const nodeData = this.saveNode(child, root, scene, modelAssetIds, audioAssetIds);
					if (nodeData)
					{
						sceneData.nodes.push(nodeData);
					}
				}
				else if (child.userData.saveModifications)
				{
					this.saveObjectModifications(child);
				}
			});
		}

		for await (const sceneData of scenesSaveData)
		{
			await jsZip.file(`scenes/${sceneData.guid}.json`, JSON.stringify(sceneData));
		}

		scenesSaveData = [];

		const referencedAssetIds = new Set<string>(audioAssetIds.concat(modelAssetIds));
		const materialSaveData = MaterialAsset.saveMaterialData(referencedAssetIds);
		const ITrackerSaveData = ARTracker.save(referencedAssetIds, false, modelAssetIds, materialSaveData.textures) as ITrackerSaveData[];
		const uiSaveData = this.saveUIData(referencedAssetIds);
		const visageSaveData = this.saveNewUIData(referencedAssetIds);
		const webviews = this.saveWebviewData();
		const scenarioSaveData = Scenario.save();
		const checklistSaveData = ProjectSaver.saveChecklistData();
		const quizSettingsSaveData = this.saveQuizSettings();
		const IStepConditionsSaveData = this.saveStepConditions();
		const timelineSaveData = Timeline.saveTimelines();
		const groupsSaveData = GroupsManager.save();
		const slideMasterData = this.saveSliderMasterData();
		const iotSaveData = this.saveIOTData();
		const attachmentSaveData = this.saveAttachmentsData(referencedAssetIds);
		const partsCatalogData = this.savePartsCatalog();

		const output: Partial<IProjectSaveData> =
		{
			version: SAVE_VERSION,
			unitScale: Engine.getInstance().getDisplayUnitScale(),
			scenesLinks: scenesToSave.map((scene) => scene.userData.guid),
			referencedAssets: Array.from(referencedAssetIds),
			modelAssets: modelAssetIds,
			audioAssets: audioAssetIds,
			materialData: materialSaveData,
			trackersData: ITrackerSaveData,
			visageData: visageSaveData,
			webviewData: webviews,
			sequenceData: scenarioSaveData,
			checklistData: checklistSaveData,
			quizSettings: quizSettingsSaveData,
			stepConditions: IStepConditionsSaveData,
			groupsData: groupsSaveData,
			slideMasterData: slideMasterData,
			iotData: iotSaveData,
			attachmentsData: attachmentSaveData,
			partsCatalog: partsCatalogData,
			envMap: Engine.getInstance().envMapAssetId,
		}

		this.objectGuidToSaveData.clear();
		this.savedSceneGuids = [];

		await jsZip.file('timelineData.json', JSON.stringify(timelineSaveData));
		await jsZip.file('uiData.json', JSON.stringify(uiSaveData));
		await jsZip.file('save.json', JSON.stringify(output));
		return { zip: jsZip, referencedAssets: output.referencedAssets };
	}

	saveNode(object: THREE.Object3D, sceneRoot: THREE.Object3D, scene: THREE.Scene, modelAssetReferences: string[], audioAssetReferences: string[]): INodeSaveData | undefined
	{
		assert(object.userData.visibility && object.userData.guid);

		const defaultLinks: IDefaultObjectLink[] = [];
		if (scene.userData.isDefaultScene)
		{
			const linkedScenes = DefaultSceneManager.getInstance().getLinkedScenesGuids(scene.userData.guid);
			for (const linkedSceneGuid of linkedScenes)
			{
				const linkedObject = DefaultSceneManager.getInstance().getLinkedObject(object.userData.guid, linkedSceneGuid);
				if (linkedObject && !linkedObject.userData.removedFromScene && this.savedSceneGuids.includes(linkedSceneGuid))
				{
					defaultLinks.push({ sceneGuid: linkedSceneGuid, objectGuid: linkedObject.userData.guid });
				}
			}
		}
		else
		{
			const defaultObject = DefaultSceneManager.getInstance().getDefaultObject(object.userData.guid);
			if (defaultObject)
			{
				this.markModifiedObject(object, SaveModificationTypes.Reference); //we want this route to always maintain guid.
				this.saveObjectModifications(object);

				if (object.userData.saveModifications &&
					object.userData.saveModifications.includes(SaveModificationTypes.Audio) && object.userData.audio &&
					object.userData.audio.clip && !audioAssetReferences.includes(object.userData.audio.clip))
				{
					audioAssetReferences.push(object.userData.audio.clip);
				}

				return;
			}
		}

		if (object.matrixAutoUpdate) object.updateMatrix();
		const materialAsset = MaterialAsset.getMaterialAssetByObjectGuid(object.userData.guid);
		const customMetadataKeys = object.userData.metaData ? Object.fromEntries(Object.entries(object.userData.metaData as IMetaDataObject).filter(([key, prop]) => !prop.readonly)) : {};

		const output: INodeSaveData =
		{
			guid: object.userData.guid,
			name: object.name,
			visibility: object.userData.visibility,
			position: object.position.toArray(),
			rotation: object.rotation.toArray(),
			scale: object.scale.toArray(),
			...(object.parent !== sceneRoot &&
			{
				...(object.parent?.userData.markedSaveRoot && { parent: object.parent?.userData.guid }),
				...(!object.parent?.userData.markedSaveRoot &&
				{
					parentSaveRoot: this.getParentSaveRoot(object).userData.guid,
					nodeIndex: object.parent?.userData.modelData.nodeIndex,
					primitiveIndex: object.parent?.userData.modelData.primitiveIndex
				}),
			}),
			...(materialAsset ? { materialAssetId: materialAsset.guid } : {}),
			...(object.userData.assetId && { assetId: object.userData.assetId }),
			...(object.userData.unlinkedDefaultObjectGuid && { unlinkedDefaultObjectGuid: object.userData.unlinkedDefaultObjectGuid }),
			...(object.userData.removedSubNodes?.length && { removedSubNodes: object.userData.removedSubNodes }),
			...(object.userData.groups && { groups: object.userData.groups }),
			...(object.userData.modelData && { modelData: object.userData.modelData }),
			...(object.userData.annotation && { annotation: object.userData.annotation }),
			...((object.userData.triggers && object.userData.triggers.length) && { triggers: object.userData.triggers }),
			...(object.userData.billboard && { billboard: object.userData.billboard }),
			...(object.userData.partsCatalog && { partsCatalog: object.userData.partsCatalog }),
			...(object.userData.primitiveType && { primitiveType: object.userData.primitiveType, primitiveScale: object.userData.primitiveScale }),
			...(object.userData.text3d && { text3d: object.userData.text3d }),
			...(object.userData.light && {
				light:
				{
					type: object.userData.lightType,
					color: object.userData.lightColor,
					intensity: object.userData.lightIntensity,
				}
			}),
			...(object.userData.audio && {
				audio:
				{
					is3D: object.userData.audio.is3D,
					volume: object.userData.audio.volume,
					clip: object.userData.audio.clip,
					range: object.userData.audio.range,
				}
			}),
			...(object.userData.mediaPanel && {
				mediaPanel:
				{
					aspect: object.userData.mediaPanel.aspect,
					scale: object.userData.mediaPanel.scale,
				}
			}),
			...(object.userData.camera && {
				camera:
				{
					nearplane: object.userData.camera.nearplane,
					farPlane: object.userData.camera.farPlane,
					fov: object.userData.camera.fov,
					orthographicSize: object.userData.camera.orthographicSize,
					aspect: object.userData.camera.aspect,
					projection: object.userData.camera.projection,
					isInteractiveModeCamera: object.userData.isInteractiveModeCamera,
					...(object.userData.preInteractiveBackupValues && { preInteractiveBackupValues: object.userData.preInteractiveBackupValues }),
					focusPoint: object.userData.interactiveCameraFocusPoint,
				}
			}),
			...((object.userData.pivot && !MathUtils.areVectorsEqual(object.userData.pivot.relativeOffset, { x: 0, y: 0, z: 0 })) && {
				pivot:
				{
					relativeOffset: object.userData.pivot.relativeOffset,
					geometryOffset: object.userData.pivot.geometryOffset,
					space: object.userData.pivot.space,
				}
			}),
			...(Object.keys(customMetadataKeys).length &&
			{
				metaData: customMetadataKeys
			}),
			...(defaultLinks.length && {
				defaultObject:
				{
					links: defaultLinks,
				}
			}),
		};

		if (object.userData.saveModifications && object.userData.saveModifications.includes(SaveModificationTypes.ReorderedChildren) && object.children.length)
		{
			output.reorderedChildrenIdentifiers = [];
			for (const child of object.children)
			{
				if (child.userData.hideInHierarchy) continue;
				if (child.userData.markedSaveRoot)
				{
					output.reorderedChildrenIdentifiers.push({ guid: child.userData.guid });
				}
				else
				{
					output.reorderedChildrenIdentifiers.push({ nodeIndex: child.userData.modelData.nodeIndex, primitiveIndex: child.userData.modelData.primitiveIndex });
				}
			}
		}

		this.objectGuidToSaveData.set(object.userData.guid, output);
		if (output.assetId && !modelAssetReferences.includes(output.assetId)) modelAssetReferences.push(output.assetId);
		if (output.audio && output.audio.clip && !audioAssetReferences.includes(output.audio.clip)) audioAssetReferences.push(output.audio.clip);
		return output;
	}

	saveObjectModifications(object: THREE.Object3D): void
	{
		let nodeIndex: number | undefined = undefined;
		let primitiveIndex: number | undefined = undefined;
		let rootParent = object;
		if (!object.userData.markedSaveRoot)
		{
			assert(object.userData.modelData);
			nodeIndex = object.userData.modelData.nodeIndex;
			primitiveIndex = object.userData.modelData.primitiveIndex;
			rootParent = this.getParentSaveRoot(object);
		}

		let modifiedChildrenRootNode = this.objectGuidToSaveData.get(rootParent.userData.guid) as IModifiedChildrenRootNode;
		if (!modifiedChildrenRootNode)
		{
			//if the savedRoot isn't in the bank, it means its a linked-object that didnt recieve its own node.
			//we must find the default object node and write assign the data location to the correct default-object-link which corresponds to this linked object
			const defaultObject = DefaultSceneManager.getInstance().getDefaultObject(rootParent.userData.guid);
			assert(defaultObject);
			if (defaultObject)
			{
				const defaultObjectNode = this.objectGuidToSaveData.get(defaultObject.userData.guid);
				if(defaultObjectNode && defaultObjectNode.defaultObject)
				{
					for (const objectLink of defaultObjectNode.defaultObject.links)
					{
						if (objectLink.objectGuid === rootParent.userData.guid)
						{
							modifiedChildrenRootNode = objectLink;
							break;
						}
					}
				}
			}
		}

		assert(modifiedChildrenRootNode);
		if (!modifiedChildrenRootNode.modifiedChildren) modifiedChildrenRootNode.modifiedChildren = {};
		assert(!modifiedChildrenRootNode.modifiedChildren[object.userData.guid]);

		modifiedChildrenRootNode.modifiedChildren[object.userData.guid] =
		{
			...(nodeIndex !== undefined && { nodeIndex: nodeIndex }),
			...(primitiveIndex !== undefined && { primitiveIndex: primitiveIndex }),
			...(object.userData.removedSubNodes?.length && { removedSubNodes: object.userData.removedSubNodes }),
			...(object.userData.saveModifications?.length && { modifications: [] }),
		};

		if (object.userData.saveModifications)
		{
			let hasPivotModification = false;
			for (const modificationType of object.userData.saveModifications as SaveModificationTypes[])
			{
				let value: any;
				switch (modificationType)
				{
					case SaveModificationTypes.Position:
						value = object.position.toArray();
						break;
					case SaveModificationTypes.Rotation:
						value = object.quaternion.toArray();
						break;
					case SaveModificationTypes.Scale:
						value = object.scale.toArray();
						break;
					case SaveModificationTypes.Name:
						value = object.name;
						break;
					case SaveModificationTypes.Visibility:
						value = object.userData.visibility;
						break;
					case SaveModificationTypes.Material:
						const materialAsset = MaterialAsset.getMaterialAssetByObjectGuid(object.userData.guid);
						value = materialAsset?.guid;
						break;
					case SaveModificationTypes.Groups:
						value = object.userData.groups;
						break;
					case SaveModificationTypes.Text3D:
						value = object.userData.text3d;
						break;
					case SaveModificationTypes.Camera:
						value =
						{
							nearplane: object.userData.camera.nearplane,
							farPlane: object.userData.camera.farPlane,
							fov: object.userData.camera.fov,
							orthographicSize: object.userData.camera.orthographicSize,
							aspect: object.userData.camera.aspect,
							projection: object.userData.camera.projection,
							isInteractiveModeCamera: object.userData.isInteractiveModeCamera,
							...(object.userData.preInteractiveBackupValues && { preInteractiveBackupValues: object.userData.preInteractiveBackupValues }),
							focusPoint: object.userData.interactiveCameraFocusPoint,
						};
						//might as well maintainGuid for all cameras as they can be refered to by UI
						modifiedChildrenRootNode.modifiedChildren[object.userData.guid].maintainGuid = true;
						break;
					case SaveModificationTypes.Light:
						value =
						{
							type: object.userData.lightType,
							color: object.userData.lightColor,
							intensity: object.userData.lightIntensity,
						};
						break;
					case SaveModificationTypes.Audio:
						value =
						{
							is3D: object.userData.audio.is3D,
							volume: object.userData.audio.volume,
							clip: object.userData.audio.clip,
							range: object.userData.audio.range,
						};
						break;
					case SaveModificationTypes.Callout:
						if (!object.userData.annotation) continue;
						value = object.userData.annotation;
						break;
					case SaveModificationTypes.Trigger:
						if (!object.userData.triggers) continue;
						value = object.userData.triggers;
						break;
					case SaveModificationTypes.Billboard:
						if (!object.userData.billboard) continue;
						value = object.userData.billboard;
						break;
					case SaveModificationTypes.Metadata:
						value = object.userData.metaData ? Object.fromEntries(Object.entries(object.userData.metaData as IMetaDataObject).filter(([key, prop]) => !prop.readonly)) : {};
						break;
					case SaveModificationTypes.PartsCatalog:
						if (!object.userData.partsCatalog) continue;
						value = object.userData.partsCatalog;
						break;
					case SaveModificationTypes.ReorderedChildren:
						{
							if (!object.children.length) continue;
							value = [];
							for (const child of object.children)
							{
								if (child.userData.hideInHierarchy) continue;
								if (child.userData.markedSaveRoot)
								{
									value.push({ guid: child.userData.guid });
								}
								else
								{
									value.push({ nodeIndex: child.userData.modelData.nodeIndex, primitiveIndex: child.userData.modelData.primitiveIndex });
								}
							}
						}
						break;
					case SaveModificationTypes.Pivot:
						if (object.userData.pivot) hasPivotModification = true;
						continue;
					case SaveModificationTypes.Reference:
						modifiedChildrenRootNode.modifiedChildren[object.userData.guid].maintainGuid = true;
						continue;
					default:
						assert(false);
						break;
				}

				modifiedChildrenRootNode.modifiedChildren[object.userData.guid].modifications!.push({ type: modificationType, value: value });
			}

			if (hasPivotModification) //pivot changes must be applied after any transform-related modifcations. this insures the pivot change is at the end of the list
			{
				const value =
				{
					relativeOffset: object.userData.pivot.relativeOffset,
					geometryOffset: object.userData.pivot.geometryOffset,
					space: object.userData.pivot.space,
				};
				modifiedChildrenRootNode.modifiedChildren[object.userData.guid].modifications!.push({ type: SaveModificationTypes.Pivot, value: value });
			}
		}
	}

	getParentSaveRoot(object: THREE.Object3D): THREE.Object3D
	{
		let rootParent = object.parent!;
		while (!rootParent.userData.markedSaveRoot)
		{
			rootParent = rootParent.parent!;
		}
		return rootParent;
	}

	onPaste(objects: THREE.Object3D[], sourceObjects: THREE.Object3D[]): void
	{
		for (let i = 0; i < objects.length; i++)
		{
			const object = objects[i];
			if (object.userData.markedSaveRoot || object instanceof ARTracker) continue;

			this.markAsSaveRoot(object);

			if (DefaultSceneManager.getInstance().isDefaultObject(object.userData.guid))
			{
				const linkedObjects = DefaultSceneManager.getInstance().getLinkedObjects(object);
				for (const object of linkedObjects)
				{
					this.markAsSaveRoot(object);
				}
			}

			if (sourceObjects[i].userData.removedFromScene) continue;

			const originalParentRoot = this.getParentSaveRoot(sourceObjects[i]);
			if (originalParentRoot.userData.removedSubNodes?.length)
			{
				object.userData.removedSubNodes = [];
				for (const removedSubNode of originalParentRoot.userData.removedSubNodes)
				{
					object.userData.removedSubNodes.push(Object.assign({}, removedSubNode));
				}
			}
		}
	}

	onReparent(object: THREE.Object3D, newParent: THREE.Object3D): void
	{
		if (!newParent.parent || newParent.parent.type !== 'Scene')
		{
			this.markModifiedObject(newParent, SaveModificationTypes.Reference);
		}
		if (DefaultSceneManager.getInstance().getDefaultObject(newParent.userData.guid) && !DefaultSceneManager.getInstance().getDefaultObject(object.userData.guid))
		{
			this.markModifiedObject(newParent, SaveModificationTypes.ReorderedChildren);
		}

		if (!object.userData.markedSaveRoot)
		{
			assert(object.userData.modelData);
			const rootParent = this.getParentSaveRoot(object);
			if (!rootParent.userData.removedSubNodes) rootParent.userData.removedSubNodes = [];

			object.userData.originalParentGuid = object.parent!.userData.guid;
			object.userData.removedSubNodes = [];
			for (const removedSubNode of rootParent.userData.removedSubNodes)
			{
				object.userData.removedSubNodes.push(Object.assign({}, removedSubNode));
			}

			rootParent.userData.removedSubNodes.push({ nodeIndex: object.userData.modelData.nodeIndex, primitiveIndex: object.userData.modelData.primitiveIndex });
			this.markAsSaveRoot(object);
		}
		else if (object.userData.originalParentGuid === newParent.userData.guid)
		{
			assert(object.userData.modelData);
			const rootParent = newParent.userData.markedSaveRoot ? newParent : this.getParentSaveRoot(newParent);
			assert(rootParent.userData.removedSubNodes);

			for (let i = 0; i < rootParent.userData.removedSubNodes.length; i++)
			{
				const removedNode = rootParent.userData.removedSubNodes[i];
				if (removedNode.nodeIndex === object.userData.modelData.nodeIndex && removedNode.primitiveIndex === object.userData.modelData.primitiveIndex)
				{
					rootParent.userData.removedSubNodes.splice(i, 1);
					break;
				}
			}

			delete object.userData.markedSaveRoot;
			delete object.userData.removedSubNodes;
			delete object.userData.originalParentGuid;
		}
	}

	processUnlinkedObjects(object: THREE.Object3D): void
	{
		//when an object gets re-linked with its default object, it also loses its saveRoot status.
		//that means that potentially, it might have had children which relied on that status in order for their data to be saved
		//we must traverse the child tree and look for any unlinked object which was not re-linked as part of the re-linking process
		//that object must be marked as a saveRoot

		if (object.userData.markedSaveRoot) return;

		const defaultObject = DefaultSceneManager.getInstance().getDefaultObject(object.userData.guid);
		if (!defaultObject)
		{
			this.markAsSaveRoot(object);
			const parentRoot = this.getParentSaveRoot(object);
			if (parentRoot.userData.removedSubNodes?.length)
			{
				object.userData.removedSubNodes = [];
				for (const removedSubNode of parentRoot.userData.removedSubNodes)
				{
					object.userData.removedSubNodes.push(Object.assign({}, removedSubNode));
				}
			}
			this.markModifiedObject(object.parent!, SaveModificationTypes.Reference);
		}
		else
		{
			for (const child of object.children)
			{
				if (!child.userData.hideInHierarchy) this.processUnlinkedObjects(child);
			}
		}
	}

	saveSliderMasterData(): { [key: string]: IUITemplateDefinition }
	{
		const output: { [key: string]: IUITemplateDefinition } = JSON.parse(JSON.stringify(store.getState().uiModule.slideMaster));
		delete output['Custom']; //delete un-needed presets if they exist
		delete output['None'];

		return output;
	}

	saveUIData(referencedAssetsList: Set<string>, isExport = false): UIData
	{
		//#region add Blank UI in trackers steps for app backwards compitability
		if (isExport)
		{
			//console.log("Preparing CPS for export with some blank UI");
			const slideTemplate = { ...store.getState().uiModule.slideMaster[TemplateTypes.BLANK], slide: TemplateTypes.BLANK, synced: true };
			const uiTemplate = {
				[TemplateTypes.BLANK]: {
					NextStep: { ...slideTemplate[TemplateTypes.BLANK].NextStep },
					PreviousStep: { ...slideTemplate[TemplateTypes.BLANK].PreviousStep },
					Voice: { ...slideTemplate[TemplateTypes.BLANK].Voice },
				},
				active: slideTemplate.active,
				slide: TemplateTypes.BLANK,
				synced: false,
			};

			//console.log("Finding Content Placement Steps...");
			Scenario.getSequences().forEach((sequence) =>
			{
				sequence.steps.forEach((step) =>
				{
					if (step.isTrackersStep() && sequence.trackersStep!.id === step.id)
					{
						//console.log("Found Content Placement Step ", step, "Pushing Content UI Data.");
						store.dispatch(StoreActions.ui.setFromCopy({ stepId: step.id, content: { ...uiTemplate } }));
					}
				});
			});
		}
		//#endregion

		const output: UIData = JSON.parse(JSON.stringify(store.getState().uiModule.ui)); // deep copy to avoid frozen objects

		//cleaning up deleted steps ui
		for (const stepId of Object.keys(output))
		{
			if (Scenario.getStep(stepId)?.isDeleted())
			{
				delete output[stepId];
			}
		}

		for (const stepData of Object.values(output))
		{
			if (stepData)
			{
				if (isExport)
				{
					delete stepData['type'];
					delete stepData['slide'];
					delete stepData['synced'];
					delete stepData['disabledComponents'];
					if (stepData.customMap)
					{
						stepData.customMap = stepData.customMap.map(item =>
							typeof item === 'object' && item.type ? item.type : item
						);
					}
				}

				for (const key of Object.keys(stepData))
				{
					if (key !== 'active')
					{
						const templateData = stepData[key];
						for (const subcomponentData of Object.values(templateData))
						{
							//Failsafe data additions for options and headers color
							if (subcomponentData["option 1 text color"] || subcomponentData["header 1 text color"])
							{
								if (!subcomponentData['text color'] || !subcomponentData['text color'].value)
								{
									subcomponentData['text color'] = {
										type: 'color',
										default: '#ffffff',
										value: "#ffffff"
									};//"#ffffff"
								}
								if (!subcomponentData['background color'] || !subcomponentData['background color'].value)
								{
									subcomponentData['background color'] = {
										type: 'color',
										default: '#4f4f4f',
										value: "#4f4f4f"
									};//"#4f4f4f"
								}
								if (!subcomponentData['shadow color'] || !subcomponentData['shadow color'].value)
								{
									subcomponentData['shadow color'] = {
										type: 'color',
										default: '#000000',
										value: "#000000"
									};//"#000000"
								}
								if (!subcomponentData['text alpha'] || !subcomponentData['text alpha'].value)
								{
									subcomponentData['text alpha'] = {
										type: 'range',
										default: 100,
										value: 100
									};
								}
								if (!subcomponentData['background alpha'] || !subcomponentData['background alpha'].value)
								{
									subcomponentData['background alpha'] = {
										type: 'range',
										default: 100,
										value: 100
									};
								}
								if (!subcomponentData['shadow alpha'] || !subcomponentData['shadow alpha'].value)
								{
									subcomponentData['shadow alpha'] = {
										type: 'range',
										default: 100,
										value: 100
									};
								}
								if (subcomponentData['undefined'])
								{
									delete subcomponentData['undefined'];
								}
							}

							for (const [propertyKey, propertyData] of Object.entries(subcomponentData))
							{
								if (isExport && propertyData.type === 'group')
								{
									delete subcomponentData[propertyKey];
								}
								if (isExport && propertyData.changed)
								{
									delete propertyData.changed;
								}

								if (isExport && propertyKey.endsWith('link'))
								{
									if (!propertyData.value) { continue; }

									const step = Scenario.getStep(propertyData.value as string);
									if (!step || step.isDisabledTrackersStep())
									{
										propertyData.value = "";
									}
								}

								if ((propertyData.type === 'image' || propertyData.type === 'video' || propertyData.type === 'audio') &&
									propertyData.value && propertyData.value !== '' && propertyData.value.toString().toLowerCase() !== 'none')
								{
									//need to fetch the inset type which is a different key
									const propertyNameArray = propertyKey.split(' '); //splitting the key to find the inset and it's number
									if (propertyNameArray[0] === 'inset')
									{
										//generating the inset type key using the name array
										const propertyTypeData = subcomponentData[propertyNameArray[0] + ' ' + propertyNameArray[1] + ' type'];
										if ((propertyTypeData.value === 'invisible') || (propertyTypeData.value !== propertyData.type && propertyData.type !== 'image'))
										{
											delete subcomponentData[propertyKey]['value'];
											continue;
										}
									}
									
									referencedAssetsList.add(propertyData.value.toString());
								}

								// WC20-2418: when value is '' the app ui will crash, so we remove it here
								if (propertyData.type === 'camera' && !propertyData.value)
								{
									delete subcomponentData[propertyKey]['value'];
								}
							}
						}
					}
				}
			}
		}

		return output;
	}

	saveNewUIData(referencedAssetsList: Set<string>)
	{
		const visageData: IVisageSaveData =
		{
			stepUIData: JsonCopy(store.getState().visageModule.stepUIData),
			stepTemplateMap: JsonCopy(store.getState().visageModule.stepTemplateMap),
			uiTemplateData: JsonCopy(store.getState().visageModule.uiTemplateData),
			cameraCanvasPairings: JsonCopy(store.getState().visageModule.cameraCanvasPairings),
		};

		// mark referenced assets
		for (const uiData of Object.values<IGrapesJSProjectData>(visageData.stepUIData))
		{
			const usedAssets = getAssetsFromGrapesJS(uiData);
			for (const asset of usedAssets)
			{
				referencedAssetsList.add(asset);
			}
		}

		// remove "previous state" from template data. it shouldn't be in the save file
		for (const templateData of Object.values(visageData.uiTemplateData))
		{
			if (templateData.previousState)
			{
				delete templateData.previousState;
			}
		}

		return visageData;
	}

	saveWebviewData()
	{
		const webViews = {};
		const stepUIData = JsonCopy(store.getState().visageModule.stepUIData);
		for (const stepId of Object.keys(stepUIData))
		{
			if (!Scenario.getStep(stepId)?.isDeleted())
			{
				webViews[stepId] = [
					{
						id: stepId,
						cutouts: [],
						assets: getAssetsFromGrapesJS(stepUIData[stepId]).map(assetId => AssetUtility.getAssetName(assetId)),
					},
				];
			}
		}

		return webViews;
	}

	saveIOTData(): any //TODO not any :/
	{
		const output = JsonCopy(store.getState().iotModule); // deep copy to avoid frozen objects
		delete output["connected"];
		delete output["tags"];
		return output;
	}

	static saveChecklistData(): IChecklistData
	{
		const output: IChecklistData = JsonCopy(store.getState().checklistModule.checklist); // deep copy to avoid frozen objects

		//cleaning up deleted steps checklists
		for (const stepId of Object.keys(output))
		{
			const step = Scenario.getStep(stepId);
			if (!output[stepId] || Object.keys(output[stepId]).length <= 0 || !step || step.isDeleted())
			{
				delete output[stepId];
			}
		}

		return output;
	}

	saveAttachmentsData(referencedAssetsList: Set<string>): IAttachements
	{
		const output: IAttachements = JsonCopy(store.getState().attachmentsModule.attachments); // deep copy to avoid frozen objects

		for (const stepId of Object.keys(output))
		{
			//cleaning up deleted steps data
			if (Scenario.getStep(stepId)?.isDeleted())
			{
				delete output[stepId];
				continue;
			}

			// don't save steps with no attachments
			const stepAttachments = Object.values(output[stepId]);
			if (stepAttachments.filter(attachmentId => (attachmentId && attachmentId !== "")).length <= 0)
			{
				delete output[stepId];
				continue;
			}

			// adding attachment assets to the referenced assets list
			for (const attachmentId of stepAttachments)
			{
				referencedAssetsList.add(attachmentId);
			}
		}

		return output;
	}

	saveQuizSettings(): any //TODO not any :/
	{
		const output = JSON.parse(JSON.stringify(store.getState().checklistModule.quizSettings)); // deep copy to avoid frozen objects
		return output;
	}

	saveStepConditions(): IStepConditionsSaveData
	{
		const output = JSON.parse(JSON.stringify(store.getState().scenarioModule.stepConditions)) as IStepConditionsSaveData; // deep copy to avoid frozen objects
		return output;
	}

	savePartsCatalog()
	{
		const output = JSON.parse(JSON.stringify(store.getState().partsCatalogModule.catalogs)); // deep copy to avoid frozen objects

		//cleaning up deleted steps catalogs
		for (const stepId of Object.keys(output))
		{
			if (!output[stepId] || //key exists but is null or undefined
				Object.keys(output[stepId]).length <= 0 || //key exists but is empty
				!Scenario.getStep(stepId) || //data exists but the step doesn't
				Scenario.getStep(stepId)?.isDeleted() //data exists but the step is flagged as deleted
			)
			{
				delete output[stepId];
				continue;
			}

			for (const item of output[stepId].partData.metadata)
			{
				if (item.id) delete item.id;
			}
		}

		return output;
	}
}
